import React, { createContext, useContext, useMemo, useEffect, useState, ReactNode } from 'react';
import { subscribeToPrograms } from '@/components/drill-planner/services/programs-api';
import { Program } from '@/components/drill-planner/types/program-types';
import { useClient } from './client-context';
import { useAuth } from '@/contexts/AuthContext';

export interface PlanAssignment {
  dayIndex: number;
  dayName: string;
  slotId: string;
  slotLabel: string;
}
import logger from '@/lib/logging';

// Debug context identity
const CONTEXT_ID = Math.random().toString(36).substring(7);
logger.debug(`[PlanContext] Module loaded. ID: ${CONTEXT_ID}`);

interface PlanContextValue {
  programs: Program[];
  programsLoading: boolean;
  activePlanId: string | null;
  activePlan: Program | null;
  setActivePlanId: (planId: string | null) => void;
  assignmentsByDrill: Map<string, PlanAssignment[]>;
  overrideActivePlan: (plan: Program) => void;
}

const PlanContext = createContext<PlanContextValue | undefined>(undefined);

function getStorageKey(clientId: string | null) {
  return clientId ? `selectedPlan:${clientId}` : null;
}

export const PlanProvider = ({ children }: { children: ReactNode }) => {
  useEffect(() => {
    logger.debug(`[PlanContext] Provider MOUNTED. ModuleID: ${CONTEXT_ID}`);
    return () => logger.debug(`[PlanContext] Provider UNMOUNTED. ModuleID: ${CONTEXT_ID}`);
  }, []);

  const { client } = useClient();
  const { serverUser, loading: authLoading } = useAuth();
  const [allPrograms, setAllPrograms] = useState<Program[]>([]);
  const [programsLoading, setProgramsLoading] = useState(true);
  const [activePlanId, setActivePlanIdState] = useState<string | null>(null);
  const [activePlanOverride, setActivePlanOverride] = useState<Program | null>(null);

  useEffect(() => {
    if (authLoading) {
      return;
    }
    if (!serverUser?.userId) {
      setAllPrograms([]);
      setProgramsLoading(false);
      return;
    }

    setProgramsLoading(true);
    const unsubscribe = subscribeToPrograms(
      (programs) => {
        setAllPrograms(programs);
        setProgramsLoading(false);
      },
      undefined,
      60_000,
      (error) => {
        logger.error('Error fetching programs via API:', error);
        setProgramsLoading(false);
      }
    );

    return unsubscribe;
  }, [authLoading, serverUser?.userId]);

  const storageKey = getStorageKey(client?.id ?? null);

  const planFromPrograms = useMemo(() => {
    if (!activePlanId) return null;
    return (
      allPrograms.find((program) => {
        if (!program.clientId) {
          return client ? false : program.id === activePlanId;
        }
        return program.id === activePlanId;
      }) ?? null
    );
  }, [allPrograms, activePlanId, client]);

  const programs = useMemo(() => {
    if (!client) return [];
    return allPrograms
      .filter(program => {
        if (!program.clientId) {
          return client ? false : true;
        }
        return program.clientId === client.id;
      })
      .sort((a, b) => {
        const dateA = new Date(a.updatedAt || a.createdAt || 0).getTime();
        const dateB = new Date(b.updatedAt || b.createdAt || 0).getTime();
        return dateB - dateA;
      });
  }, [allPrograms, client]);

  useEffect(() => {
    if (!client || !storageKey) {
      setActivePlanIdState(null);
      return;
    }
    try {
      const storedPlanId = localStorage.getItem(storageKey);
      if (storedPlanId) {
        setActivePlanIdState(storedPlanId);
      } else {
        setActivePlanIdState(null);
      }
    } catch (error) {
      logger.error('Failed to load selected plan from storage:', error);
      setActivePlanIdState(null);
    }
  }, [client, storageKey]);

  useEffect(() => {
    if (!storageKey) return;
    try {
      if (activePlanId) {
        localStorage.setItem(storageKey, activePlanId);
      } else {
        localStorage.removeItem(storageKey);
      }
    } catch (error) {
      logger.error('Failed to persist selected plan:', error);
    }
  }, [activePlanId, storageKey]);

  useEffect(() => {
    if (!activePlanId) return;
    if (!programs.some(program => program.id === activePlanId)) {
      setActivePlanIdState(null);
    }
  }, [programs, activePlanId]);

  useEffect(() => {
    if (!planFromPrograms) {
      setActivePlanOverride(null);
      return;
    }

    setActivePlanOverride((prev) => {
      if (!prev || prev.id !== planFromPrograms.id) {
        return prev;
      }

      const prevUpdatedAt = prev.updatedAt ? new Date(prev.updatedAt).getTime() : 0;
      const programUpdatedAt = planFromPrograms.updatedAt ? new Date(planFromPrograms.updatedAt).getTime() : 0;

      if (programUpdatedAt >= prevUpdatedAt) {
        return null;
      }

      return prev;
    });
  }, [planFromPrograms]);

  const activePlan = useMemo(() => {
    if (activePlanOverride && activePlanOverride.id === activePlanId) {
      return activePlanOverride;
    }
    return planFromPrograms;
  }, [activePlanOverride, planFromPrograms, activePlanId]);

  const assignmentsByDrill = useMemo(() => {
    const map = new Map<string, PlanAssignment[]>();
    if (!activePlan) return map;

    activePlan.days.forEach((day, dayIndex) => {
      Object.entries(day.slots || {}).forEach(([slotId, slot]) => {
        if (!slot?.drillId) return;
        const slotLabel = activePlan.slotLabels?.[slotId] || slotId;
        const assignment: PlanAssignment = {
          dayIndex,
          dayName: day.name || `Day ${dayIndex + 1}`,
          slotId,
          slotLabel,
        };
        const existing = map.get(slot.drillId) ?? [];
        existing.push(assignment);
        map.set(slot.drillId, existing);
      });
    });

    return map;
  }, [activePlan]);

  const setActivePlanId = (planId: string | null) => {
    setActivePlanIdState(planId);
  };

  const value: PlanContextValue = {
    programs,
    programsLoading,
    activePlanId,
    activePlan,
    setActivePlanId,
    assignmentsByDrill,
    overrideActivePlan: (plan: Program) => setActivePlanOverride(plan),
  };

  return <PlanContext.Provider value={value}>{children}</PlanContext.Provider>;
};



export const usePlan = () => {
  const context = useContext(PlanContext);
  if (!context) {
    logger.error(`[PlanContext] usePlan FAILED. ModuleID: ${CONTEXT_ID}. Context is undefined.`);
    throw new Error('usePlan must be used within a PlanProvider');
  }
  return context;
};

